import { buildFeedDownChild, buildIsDirtyChild, bulkSubscribe, bulkSubscribeSingle, bulkSubscribeWithContext, feedValueDown, setupIsDirtySubscriptionsV2 } from "@/utils/modelState";
import { updateDatePreservingTime } from "@/utils/utils";
import { add, startOfDay } from 'date-fns';
import { createReservationQuoteStatus, deserializeReservationStatus, hydrateReservationStatus } from "./ReservationStatus";
import { hydrateReservationArrivalTransferDetails, hydrateReservationDepartureTransferDetails } from "./model.TransferDetails";
import type { ModelContext } from "./model.context";
import { updateAmountPayableTotal, updateCanRelease, updateStayDates } from './model.roomsAndTotals';
import { dropOpenPriceOnRoomTypeChange } from "./utils";
export function initializeModel1(context: ModelContext) {
    // Bind reservation for company cache error reporting
    context.reservationRef.current = context.reservation;
    context.reservation.ReservationStatus.setValue(context.reservationStatus ? deserializeReservationStatus(context.reservation, context.reservationStatus) : createReservationQuoteStatus());
    context.reservation.ReservationStatus.commit();
    // This will copy the value when it changes and push the value down into child values
    feedValueDown(context.reservation.ArrivalDate, [
        buildFeedDownChild(context.reservation.RoomBookings, rb => rb.CheckIn, (existing, incoming) => {
            return updateDatePreservingTime(existing, incoming);
        }),
        buildFeedDownChild(context.reservation.ProgramBookings, pb => pb.Start, (existing, incoming) => {
            if (context.reservation.RoomBookings.toArray().length > 0) {
                return updateDatePreservingTime(existing, startOfDay(add(incoming, { days: 1 })));
            }
            return updateDatePreservingTime(existing, startOfDay(incoming));
        })
    ]);
    // This will copy the value when it changes and push the value down into child values
    feedValueDown(context.reservation.DepartureDate, [
        buildFeedDownChild(context.reservation.RoomBookings, rb => rb.CheckOut, (existing, incoming) => {
            return updateDatePreservingTime(existing, incoming);
        }),
        buildFeedDownChild(context.reservation.ProgramBookings, pb => pb.End, (existing, incoming) => {
            return updateDatePreservingTime(existing, incoming);
        }),
    ]);
    context.reservation.ArrivalDate.subscribe(() => updateStayDates(context.reservation));
    context.reservation.ArrivalDate.subscribe(() => updateCanRelease(context.reservation));
    context.reservation.DepartureDate.subscribe(() => updateStayDates(context.reservation));
    context.reservation.Payments.subscribe(() => updateCanRelease(context.reservation));
    context.reservation.Refunds.subscribe(() => updateCanRelease(context.reservation));
    context.reservation.PosItems.subscribe(() => updateCanRelease(context.reservation));
    // This will subscribe to all the specified properties and call the function when they change
    bulkSubscribe(context.reservation.RoomBookings, [
        rb => rb.CheckIn,
        rb => rb.CheckOut,
    ], () => updateStayDates(context.reservation));
    // Ensure room location id is reset when room type is changed
    bulkSubscribeWithContext(context.reservation.RoomBookings, rb => {
        return {
            context: rb,
            dispose: () => { }
        };
    }, [
        rb => rb.RoomType
    ], rb => {
        if (!rb.RoomType.isDirty) {
            rb.RoomLocationId.rollback();
        }
        else {
            rb.RoomLocationId.setValue(null);
        }
    });
    // This will subscribe to all the specified properties and call the function when they change
    bulkSubscribe(context.reservation.ProgramBookings, [
        pb => pb.Start,
        pb => pb.End,
    ], () => updateStayDates(context.reservation));
    // Setup the whole model IsDirty set of subscriptions
    setupIsDirtySubscriptionsV2(context.reservation, [
        context.reservation.InquirySource.Value,
        // Which properties to watch
        context.reservation.Person,
        context.reservation.BillingContact,
        context.reservation.ArrivalDate,
        context.reservation.DepartureDate,
        context.reservation.RoomBookings.isDirty,
        context.reservation.SomeRoomBookingsDirty,
        context.reservation.ProgramBookings.isDirty,
        context.reservation.SomeProgramBookingsDirty,
        context.reservation.AppliedVouchers,
        context.reservation.ReservationStatusIsDirty,
        context.reservation.QuoteIsDirty,
        context.reservation.ArrivalTransferDetailsIsDirty,
        context.reservation.DepartureTransferDetailsIsDirty,
    ], [
        r => r.InquirySource.Value.isDirty,
        // Which isDirty values should contribute to the overall isDirty value
        r => r.Person.isDirty,
        r => r.BillingContact?.isDirty ?? false,
        r => r.ArrivalDate.isDirty,
        r => r.DepartureDate.isDirty,
        r => r.RoomBookings.isDirty.value,
        r => r.SomeRoomBookingsDirty.value,
        r => r.ProgramBookings.isDirty.value,
        r => r.SomeProgramBookingsDirty.value,
        r => r.AppliedVouchers.isDirty,
        r => r.ReservationStatusIsDirty.value,
        r => r.QuoteIsDirty.value,
        r => r.ArrivalTransferDetailsIsDirty.value,
        r => r.DepartureTransferDetailsIsDirty.value,
    ], [
        // RoomBookings
        buildIsDirtyChild(
            // Which collection on the parent model should we watch
            context.reservation.RoomBookings,
            // Which property on the parent model should represent whether any items in the child collection is dirty
            context.reservation.SomeRoomBookingsDirty,
            // Which property on the child model should represent whether the child item is dirty
            rb => rb.IsDirty, rb => setupIsDirtySubscriptionsV2(rb, [
                rb.RoomType,
                rb.RoomLocationId,
                rb.CheckIn,
                rb.CheckOut,
                rb.Adults,
                rb.Children,
                rb.People.isDirty,
                rb.SomePeopleDirty,
                rb.NoShow,
                rb.NoShowDirty,
                rb.NoCharge,
                rb.NoChargeDirty,
            ], [
                rb => rb.RoomType.isDirty,
                rb => rb.RoomLocationId.isDirty,
                rb => rb.CheckIn.isDirty,
                rb => rb.CheckOut.isDirty,
                rb => rb.Adults.isDirty,
                rb => rb.Children.isDirty,
                rb => rb.People.isDirty.value,
                rb => rb.SomePeopleDirty.value,
                rb => rb.NoShow.isDirty,
                rb => rb.NoShowDirty.value,
                rb => rb.NoCharge.isDirty,
                rb => rb.NoChargeDirty.value,
            ], [
                buildIsDirtyChild(rb.People, rb.SomePeopleDirty, p => p.IsDirty, p => setupIsDirtySubscriptionsV2(p, [
                    p.Person
                ], [
                    p => p.Person.isDirty
                ])),
            ])),
        // ProgramBookings
        buildIsDirtyChild(context.reservation.ProgramBookings, context.reservation.SomeProgramBookingsDirty, pb => pb.IsDirty, pb => setupIsDirtySubscriptionsV2(pb, [
            pb.ProgramType,
            pb.Start,
            pb.End,
            pb.People.isDirty,
            pb.SomePeopleDirty,
            pb.NoShow,
            pb.NoShowDirty,
            pb.NoCharge,
            pb.NoChargeDirty
        ], [
            pb => pb.ProgramType.isDirty,
            pb => pb.Start.isDirty,
            pb => pb.End.isDirty,
            rb => rb.People.isDirty.value,
            rb => rb.SomePeopleDirty.value,
            pb => pb.NoShow.isDirty,
            pb => pb.NoShowDirty.value,
            pb => pb.NoCharge.isDirty,
            pb => pb.NoChargeDirty.value
        ], [
            buildIsDirtyChild(pb.People, pb.SomePeopleDirty, p => p.IsDirty, p => setupIsDirtySubscriptionsV2(p, [
                p.Person
            ], [
                p => p.Person.isDirty
            ]))
        ]))
    ]);
    // reservation status
    hydrateReservationStatus(context.reservation);
    hydrateReservationArrivalTransferDetails(context.reservation);
    hydrateReservationDepartureTransferDetails(context.reservation);
    // Same as the bulkSubscribe but this time when a new item is detected in the list
    // we can create some context/object that we can use when changes are detected
    //bulkSubscribeWithContext(
    //    // Which collection to watch
    //    reservation.RoomBookings,
    //    // A context object to be created for every item in the list
    //    // In this case we're creating one priceChecker object per roomBooking
    //    item => {
    //        const priceChecker = createRoomBookingPriceChecker(reservation, item)
    //        // Pass down the priceChecker loading value to the roomBooking
    //        // And once the priceChecker has stopped loading, update the roomBooking price
    //        const unsubscribePriceChecker = priceChecker.isLoading.subscribe(() => {
    //            item.Price.isLoading.setValue(priceChecker.isLoading.value);
    //            if (!priceChecker.isLoading.value) {
    //                item.Price.price.setValue(priceChecker.price);
    //                item.Dates = priceChecker.dates;
    //            }
    //        });
    //        // Get the initial price on new items
    //        if (item.Price.price.value.baseAmount === 0) {
    //            // fire and forget
    //            priceChecker.refresh();
    //        }
    //        return {
    //            context: priceChecker,
    //            dispose: unsubscribePriceChecker
    //        };
    //    },
    //    // Which properties to watch for changes
    //    [
    //        rb => rb.RoomType,
    //        rb => rb.CheckIn,
    //        rb => rb.CheckOut
    //    ],
    //    // When any of the above properties change, call this..
    //    priceChecker => priceChecker.refresh());
    //bulkSubscribeWithContext(
    //    reservation.ProgramBookings,
    //    item => {
    //        const priceChecker = createProgramBookingPriceChecker(reservation, item)
    //        const unsubscribePriceChecker = priceChecker.isLoading.subscribe(() => {
    //            item.Price.isLoading.setValue(priceChecker.isLoading.value);
    //            if (!priceChecker.isLoading.value) {
    //                item.Price.price.setValue(priceChecker.price);
    //                item.Dates = priceChecker.dates;
    //            }
    //        });
    //        if (item.Price.price.value.baseAmount === 0) {
    //            // fire and forget
    //            priceChecker.refresh();
    //        }
    //        return {
    //            context: priceChecker,
    //            dispose: unsubscribePriceChecker
    //        };
    //    },
    //    [
    //        pb => pb.ProgramType,
    //        rb => rb.Start,
    //        rb => rb.End
    //    ],
    //    priceChecker => priceChecker.refresh());
    // Basis-change lifecycle: a room's room-type changing is a basis change, so its open price
    // drops unless the rate is locked. Registered BEFORE the checkPrices subscription below so it
    // runs first - the override is cleared before the price re-check reads the row, so the recompute
    // stands on the new room type's rate plan instead of echoing the stale open price.
    // See @DOCSAPP/EN/room-row-pricing-architecture for the seam definition.
    bulkSubscribeSingle(context.reservation.RoomBookings, rb => rb.RoomType, (rb, previousRoomType) => dropOpenPriceOnRoomTypeChange(rb, previousRoomType));
    bulkSubscribe(
        // Which collection to watch
        context.reservation.RoomBookings,
        // Which properties to watch for changes
        [
            rb => rb.RoomType,
            rb => rb.RoomLocationId,
            rb => rb.CheckIn,
            rb => rb.CheckOut,
            rb => rb.Adults,
            rb => rb.Children,
            rb => rb.PeopleVersion
        ],
        // When any of the above properties change, call this..
        () => context.checkPrices());
    bulkSubscribe(context.reservation.ProgramBookings, [
        pb => pb.ProgramType,
        pb => pb.Start,
        pb => pb.End,
        pb => pb.StartHalfDay,
        pb => pb.EndHalfDay,
        pb => pb.PeopleVersion
    ], () => context.checkPrices());
    context.checkPricesController = new AbortController();
    context.reservation.RoomBookings.subscribe(() => {
        context.checkRoomsWarning();
        context.checkProgramsWarning();
    });
    bulkSubscribe(
        // Which collection to watch
        context.reservation.RoomBookings,
        // Which properties to watch for changes
        [
            rb => rb.CheckIn,
            rb => rb.CheckOut
        ],
        // When any of the above properties change, call this..
        () => {
            context.checkRoomsWarning();
            context.checkProgramsWarning();
        });
    context.reservation.ProgramBookings.subscribe(() => {
        context.checkProgramsWarning();
        context.checkRoomsWarning();
    });
    bulkSubscribe(
        // Which collection to watch
        context.reservation.ProgramBookings,
        // Which properties to watch for changes
        [
            rb => rb.Start,
            rb => rb.End
        ],
        // When any of the above properties change, call this..
        () => {
            context.checkProgramsWarning();
            context.checkRoomsWarning();
        });
    context.reservation.AppliedVouchers.subscribe(() => {
        context.checkPrices();
    });
    context.reservation.SelectedRatePlan.subscribe(() => {
        context.checkPrices();
    });
    context.reservation.Person.subscribe(() => {
        context.checkPrices();
    });
    bulkSubscribe(context.reservation.RoomBookings, [i => i.CalculatedPrice], () => updateAmountPayableTotal(context.reservation));
}
